/**
 * Clients API - Client-Side Service
 * 
 * All client database operations go through server-side Express API.
 * Client-side code MUST NOT access the database directly.
 */
import type { Client, ClientSummary } from '@/lib/types';
import { apiFetch, pollEndpoint } from '@/lib/api-client';
import {
  computeClientsSummaryChangeKey,
  sortClientSummaries,
} from '@/lib/clients/summary';

/** Stable order for client-list polling: Firestore list order can vary between reads. */
function sortClientsById(clients: Client[]): Client[] {
  return [...clients].sort((a, b) => a.id.localeCompare(b.id));
}

// Response types
interface CreateClientResponse {
  success: boolean;
  clientId: string;
  message: string;
}

interface UpdateResponse {
  success: boolean;
  message: string;
}

/**
 * Create a new client
 */
export async function createClient(
  clientData: Omit<Client, 'id'> & { sourceProspectId?: string },
): Promise<string> {
  const response = await apiFetch<CreateClientResponse>('/clients', {
    method: 'POST',
    body: JSON.stringify(clientData),
  });
  return response.clientId;
}

/**
 * Get a single client by ID
 */
export async function getClient(clientId: string): Promise<Client | null> {
  try {
    const client = await apiFetch<Client>(`/clients/${clientId}`);
    return client;
  } catch (error: any) {
    if (error.message?.includes('not-found') || error.message?.includes('404')) {
      return null;
    }
    throw error;
  }
}

/**
 * Get all clients for the current user
 */
export async function getClients(): Promise<Client[]> {
  return apiFetch<Client[]>('/clients');
}

/**
 * Fetch per-client assessment/posture aggregates for the current user.
 */
export async function fetchClientsSummary(): Promise<ClientSummary[]> {
  return apiFetch<ClientSummary[]>('/clients/summary');
}

/**
 * Search clients by partial name/email (server filters by user access).
 */
export async function searchClients(query: string): Promise<Client[]> {
  const params = new URLSearchParams();
  params.set('query', query);
  return apiFetch<Client[]>(`/clients/search?${params.toString()}`);
}

/**
 * Update a client
 */
export async function updateClient(
  clientId: string,
  updates: Partial<Client>
): Promise<void> {
  await apiFetch<UpdateResponse>(`/clients/${clientId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

/**
 * Delete a client and all associated data
 */
export async function deleteClient(clientId: string): Promise<void> {
  await apiFetch<UpdateResponse>(`/clients/${clientId}`, {
    method: 'DELETE',
  });
}

/**
 * Share a client with another user
 */
export async function shareClient(
  clientId: string,
  targetUserId: string,
  permission: 'read' | 'write' = 'read'
): Promise<void> {
  await apiFetch<UpdateResponse>(`/clients/${clientId}/share`, {
    method: 'POST',
    body: JSON.stringify({ targetUserId, permission }),
  });
}

/**
 * Revoke access to a client from another user
 */
export async function revokeClientAccess(
  clientId: string,
  targetUserId: string
): Promise<void> {
  await apiFetch<UpdateResponse>(`/clients/${clientId}/revoke`, {
    method: 'POST',
    body: JSON.stringify({ targetUserId }),
  });
}

/**
 * Subscribe to clients with polling for real-time updates
 * Returns cleanup function to stop polling
 */
/**
 * Subscribe to a single client with polling for updates.
 */
export function subscribeToClient(
  clientId: string,
  callback: (client: Client | null) => void,
  intervalMs: number = 5000,
  onError?: (error: Error) => void
): () => void {
  return pollEndpoint<Client>(`/clients/${clientId}`, callback, intervalMs, onError);
}

export function subscribeClients(
  callback: (clients: Client[]) => void,
  intervalMs: number = 60000,
  onError?: (error: Error) => void
): () => void {
  return pollEndpoint<Client[]>('/clients', callback, intervalMs, onError, {
    getChangeKey: (data) => JSON.stringify(sortClientsById(data)),
    normalizeCallbackPayload: sortClientsById,
  });
}

/**
 * Subscribe to client summaries with polling (same interval as clients list).
 */
export function subscribeClientsSummary(
  callback: (summaries: ClientSummary[]) => void,
  intervalMs: number = 60000,
  onError?: (error: Error) => void
): () => void {
  return pollEndpoint<ClientSummary[]>('/clients/summary', callback, intervalMs, onError, {
    getChangeKey: computeClientsSummaryChangeKey,
    normalizeCallbackPayload: sortClientSummaries,
  });
}
